// SupplierListLayer.jsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getPaginatedSuppliers, getSupplierById, createSupplier, updateSupplier, deleteSupplier } from '../api/supplierAPI';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useSettings } from '../utils/AppSettingsContext';
import { validateForm } from '../utils/useValidator';

const SupplierListLayer = () => {
  const userProfile = useUserProfile();
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [newSupplier, setNewSupplier] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const listContainerRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const defaultCurrency = useSettings('default_currency');
  const defaultPaymentTerms = useSettings('default_payment_terms');

  const inputFields = useMemo(
    () => [
      { field: 'name', label: 'Name', type: 'text', size: 6, required: true },
      { field: 'supplierCode', label: 'Supplier Code', type: 'text', size: 6 },
      { field: 'address', label: 'Address', type: 'text', size: 12 },
      { field: 'address2', label: '', type: 'text', size: 12 },
      { field: 'state', label: 'State', type: 'text', size: 4 },
      { field: 'city', label: 'City', type: 'text', size: 4 },
      { field: 'postCode', label: 'Post Code', type: 'text', size: 4 },
      { field: 'phone', label: 'Phone Number', type: 'text', size: 6 },
      { field: 'taxNumber', label: 'Tax Number', type: 'text', size: 6 },
      { field: 'paymentTerms', label: 'Payment Terms', type: 'text', size: 6 },
      { field: 'currencyId', label: 'Default Currency', type: 'select', size: 6, api: 'currency', displayField: 'symbol', isClearable: true, required: true },
    ],
    [],
  );

  const { renderField } = useFormBuilder(inputFields, newSupplier, setNewSupplier, errors);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const pageSize = page === 1 ? 25 : 5;
        const resp = await getPaginatedSuppliers({ page, pageSize, search: searchTerm });
        setSuppliers(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newItems = resp.data.filter(p => !existingIds.has(p.id));
          const updated = [...prev, ...newItems];
          setHasMore(updated.length < resp.total);
          return updated;
        });
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [page, searchTerm, refreshKey]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        hasMore &&
        !loading
      ) {
        setPage(p => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasMore, loading]);

  const runValidation = () => {
    const newErrors = validateForm(inputFields, newSupplier);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!runValidation()) return;
    const clean = {};
    for (const key in newSupplier) {
      if (newSupplier[key] !== null && typeof newSupplier[key] !== 'object') {
        clean[key] = newSupplier[key];
      }
    }
    if (editingId) {
      await updateSupplier(editingId, clean);
    } else {
      await createSupplier(clean);
    }
    setShowModal(false);
    setEditingId(null);
    setNewSupplier({ currencyId: 1, paymentTerms: defaultPaymentTerms || '' });
    setSuppliers([]);
    setPage(1);
    setHasMore(true);
    setRefreshKey(k => k + 1);
  };

  const generateCsv = () => {
    const headers = ['Name', 'Code', 'Phone', 'City', 'Country', 'Currency'];
    const rows = suppliers.map(row => [
      row.name,
      row.supplierCode || '',
      row.phone || '',
      row.city || '',
      row.country || '',
      row.currency?.symbol || '',
    ]);
    const csvLines = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = ['Name', 'Code', 'Phone', 'City', 'Country', 'Currency'];
    const rows = suppliers.map(row => [
      row.name,
      row.supplierCode || '',
      row.phone || '',
      row.city || '',
      row.country || '',
      row.currency?.symbol || '',
    ]);
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Suppliers');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'suppliers.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'suppliers.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Export Data
            </button>
            <ul className="dropdown-menu p-12 border bg-base shadow">
              <li>
                <button className="dropdown-item" onClick={handleExportExcel}>Excel</button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleExportCSV}>CSV</button>
              </li>
            </ul>
          </div>
          {userProfile?.privileges?.includes('suppliers:create') && (
            <button
              className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
              onClick={() => {
                setNewSupplier({ currencyId: parseInt(defaultCurrency, 10) || 1, paymentTerms: defaultPaymentTerms || '' });
                setEditingId(null);
                setShowModal(true);
              }}
            >
              <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Supplier
            </button>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        <div
          className="table-responsive scroll"
          ref={listContainerRef}
          style={{ maxHeight: 'calc(100vh - 146px)' }}
        >
          <table className="table bordered-table mb-3 rounded-0">
            <thead>
              <tr>
                <th className="rounded-0">Name</th>
                <th>Code</th>
                <th>Phone</th>
                <th>City</th>
                <th>Country</th>
                <th>Currency</th>
                <th className="rounded-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.supplierCode}</td>
                  <td>{s.phone}</td>
                  <td>{s.city}</td>
                  <td>{s.country}</td>
                  <td>{s.currency?.symbol || ''}</td>
                  <td>
                    <button
                      className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                      onClick={async () => {
                        const supplier = await getSupplierById(s.id);
                        setNewSupplier({
                          ...supplier,
                          currencyId: supplier.currencyId || supplier.currency?.id,
                        });
                        setEditingId(s.id);
                        setShowModal(true);
                      }}
                    >
                      <Icon icon="ic:baseline-edit" />
                    </button>
                    <button
                      className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                      onClick={async () => {
                        if (window.confirm('Delete supplier?')) {
                          await deleteSupplier(s.id);
                          setSuppliers((prev) => prev.filter((p) => p.id !== s.id));
                        }
                      }}
                    >
                      <Icon icon="ic:baseline-delete-outline" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && suppliers.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-2">
                    No Suppliers found
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-2">
                    <span className="spinner-border spinner-border-sm" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Supplier' : 'New Supplier'}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {inputFields.map(renderField)}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}>Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}>Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierListLayer;
